import type React from "react";
import { lazy, Suspense } from "react";
import {
  BrowserRouter as Router,
  Route,
  Outlet,
  Routes,
  Navigate,
} from "react-router-dom";
import ThemeContextProvider from "./contextApi/ThemeContext";
import Dashboard from "./components/dashboards/BaseDashBoard";
import { Box, CircularProgress } from "@mui/material";
import routes from "./routes";
import { ProctoringProvider } from "./pages/student/TestEnvironment/contexts/ProctoringContext";
import { SecurityWarning } from "./components/SecurityWarning"; // ADD THIS IMPORT
import { Toaster } from "sonner";

// 🟢 1. LAZY LOAD THE NEW HACKATHON APP
// This ensures regular students don't download hackathon code
const HackathonApp = lazy(() => import("./Chaturvyuha/HackathonApp"));

// Suspense Fallback Component
const LoadingFallback = () => (
  <div>
    {" "}
    <Box
      display="flex"
      justifyContent="center"
      alignItems="center"
      height="100vh"
    >
      <CircularProgress />
    </Box>
  </div>
);

const App: React.FC = () => {
  // 🟢 2. DETECT THE SUBDOMAIN
  const hostname = window.location.hostname;
  const isHackathon =
    hostname.includes("chaturvyuha") || hostname.includes("hackathon.localhost");

  return (
    <ThemeContextProvider>
      <Router>
        {/* 🔒 MOVE SECURITY WARNING OUTSIDE OF <Routes> BUT INSIDE <Router> */}
        <SecurityWarning />

        <Suspense>
          {/* 🟢 3. THE ROUTING SWITCH */}
          {isHackathon ? (
            <HackathonApp />
          ) : (
          <Routes>
            {/* Public Routes */}
            <Route path="/" element={<routes.LandingPage />} />
            <Route path="/privacy-policy" element={<routes.PrivacyPolicy />} />
            <Route
              path="/terms-of-service"
              element={<routes.TermsOfService />}
            />
            <Route path="/cookie-policy" element={<routes.CookiePolicy />} />
            <Route path="/refund-policy" element={<routes.RefundPolicy />} />
            <Route path="/login" element={<routes.Login />} />
            <Route path="/email-login" element={<routes.EmailLogin />} />
            <Route path="/password-reset" element={<routes.ForgotPassword />} />
            <Route path="/leaderboard" element={<routes.LeaderboardPage />} />
            <Route
              path="/onboarded-companies"
              element={<routes.OnboardedCompanies />}
            />
            <Route
              path="/company-registration"
              element={<routes.CompanyRegistration />}
            />
            <Route path="/hire-us" element={<routes.HireUs />} />
            {/* --------------------------------------------------------- */}
            {/* ✅ NEW NESTED COMPANY ROUTES STRUCTURE                    */}
            {/* --------------------------------------------------------- */}

            {/* --------------------------------------------------------- */}
            <Route path="/view-jobs" element={<routes.ViewJobs />} />
            <Route
              path="/view-career-jobs"
              element={<routes.ViewCareerJobs />}
            />
            <Route
              path="/apply-career-job/:jobId"
              element={<routes.ApplyCareerJob />}
            />
            <Route path="/apply-job/:jobId" element={<routes.ApplyJob />} />

            <Route
              path="/student/assessment/:token"
              element={<routes.AssessmentTokenPage />}
            />
            <Route
              path="/student/dashboard"
              element={<routes.StudentDashboard />}
            />
            <Route
              path="/student/assessment/:assessmentId/overview"
              element={<routes.AssessmentOverview />}
            />
            {/* Proctored Assessment Flow - Wrapped in a single ProctoringProvider */}
            <Route
              path="/student/assessment/:assessmentId"
              element={
                <ProctoringProvider>
                  <Outlet />
                </ProctoringProvider>
              }
            >
              <Route
                path="onboarding"
                element={
                  <Suspense fallback={<LoadingFallback />}>
                    <routes.AssessmentOnboarding />
                  </Suspense>
                }
              />

              <Route
                path="attempt"
                element={
                  <Suspense fallback={<LoadingFallback />}>
                    <routes.AssessmentTest />
                  </Suspense>
                }
              />
            </Route>
            <Route
              path="/plans/:planId/signup"
              element={<routes.StudentForm />}
            />
            <Route
              path="/signup-success"
              element={<routes.SignupSuccessPage />}
            />

            <Route
              path="/plans/:planId/organization-type"
              element={<routes.OrganizationTypePage />}
            />
            <Route
              path="/plans/:planId/institution"
              element={<routes.InstitutionForm />}
            />
            <Route
              path="/plans/:planId/company"
              element={<routes.CompanyForm />}
            />

            {/* Payment and success routes */}
            <Route path="/payment" element={<routes.PaymentPage />} />
            <Route path="/login-plan" element={<routes.LoginPlan />} />
            <Route path="/signup-success" element={<routes.SuccessPage />} />
            <Route path="/payment-success" element={<routes.SuccessPage />} />
            <Route
              path="/problem/slug/:slug"
              element={<routes.ProblemPage />}
            />
            <Route
              path="/assessment/details/:slug"
              element={<routes.AssessmentDetailsPage />}
            />
            <Route
              path="/assessment/:assessmentslug"
              element={<routes.AssessmentHomePage />}
            />
            <Route
              path="/subscribe/plan/:id"
              element={<routes.SubscribeToPlan />}
            />
            <Route path="/paymentsuccess" element={<routes.PaymentSuccess />} />
<Route path="/company" element={<routes.CompanyLayout />}>
                {/* Redirect /company to /company/dashboard */}
                <Route index element={<Navigate to="dashboard" replace />} />

                {/* Child Routes (Render inside the Outlet of CompanyLayout) */}
                <Route path="dashboard" element={<routes.CompanyOverview />} />
                <Route
                  path="agency-drives"
                  element={<routes.AgencyJobsForCompany />}
                />
                <Route path="post-job" element={<routes.PostJob />} />
                <Route path="edit-job" element={<routes.EditJob />} />
                <Route path="applicants" element={<routes.ApplicantsView />} />
                <Route path="leaderboard" element={<routes.JobLeaderboard />} />
                <Route path="settings" element={<routes.CompanySettings />} />
                <Route
                  path="assessment-reports"
                  element={<routes.CompanyAssessmentReports />}
                />
              </Route>
            {/* Protected Routes (Wrapped in Dashboard) */}
            <Route element={<Dashboard />}>
              
              <Route path="profile" element={<routes.ProfileDashboard />} />
              <Route path="users" element={<routes.UserManagement />} />
              <Route path="users/create" element={<routes.CreateUser />} />
              <Route
                path="students/agency-linked"
                element={<routes.AgencyLinkedStudents />}
              />
              <Route
                path="students/college-linked"
                element={<routes.CollegeLinkedStudents />}
              />
              <Route
                path="students/individuals"
                element={<routes.IndividualsLinkedStudents />}
              />
              <Route
                path="client/view-candidates"
                element={<routes.ViewCandidates />}
              />
              <Route
                path="agency/applicants-analysis/:studentId"
                element={<routes.ApplicantsFitmentAnalysis />}
              />
              <Route
                path="client/add-candidates"
                element={<routes.AddCandidates />}
              />
              <Route
                path="student/jobsManagement"
                element={<routes.StudentJobsManagement />}
              />
              <Route
                path="recruitment/view-agencies"
                element={<routes.ViewAgencies />}
              />
              <Route
                path="recruitment/create-agency"
                element={<routes.CreateAgency />}
              />
              <Route
                path="client/view-employers"
                element={<routes.ViewEmployers />}
              />
              <Route
                path="client/add-employers"
                element={<routes.AddEmployer />}
              />
              <Route
                path="author-management/add-author"
                element={<routes.AddAuthor />}
              />
              <Route
                path="author-management/add-content-head"
                element={<routes.AddContentHead />}
              />
              <Route
                path="author-management/add-super-access"
                element={<routes.AddSuperAccess />}
              />
              <Route
                path="author-management/add-enterprise-admin"
                element={<routes.AddEnterpriseAdmin />}
              />
              <Route
                path="author-management/add-moderator"
                element={<routes.AddModerator />}
              />
              <Route
                path="author-management/view-authors"
                element={<routes.ViewAuthor />}
              />
              <Route
                path="author-management/view-moderator"
                element={<routes.ViewModerator />}
              />
              <Route
                path="author-management/view-content-head"
                element={<routes.ViewContentHead />}
              />
              <Route
                path="author-management/view-super-access"
                element={<routes.ViewSuperAccess />}
              />
              <Route
                path="author-management/view-enterprise-admin"
                element={<routes.ViewEnterpriseAdmin />}
              />
              <Route
                path="enterprise-admin/add-licenses"
                element={<routes.AddLicenseRequest />}
              />
              <Route
                path="enterprise-admin/view-licenses"
                element={<routes.ViewLicenseRequests />}
              />

              <Route
                path="author-management/review-licenses"
                element={<routes.AdminReviewLicenses />}
              />
              
              <Route
                path="super-admin/view-consultants"
                element={<routes.ViewConsultants />}
              />
              <Route
                path="super-admin/add-consultants"
                element={<routes.AddConsultants />}
              />{" "}
              <Route
                path="super-admin/add-business-unit"
                element={<routes.AddBusinessUnit />}
              />
              <Route path="super-admin/assign-consultants" element={<routes.AssignConsultantsToBU />} />

              <Route
                path="business-unit/add-consultants"
                element={<routes.AddConsultantsToBU />}
              />{" "}
              <Route
                path="business-unit/view-consultants"
                element={<routes.ViewConsultantsToBU />}
              />{" "}
              <Route
                path="business-unit/assign-consultants"
                element={<routes.AssignConsultants />}
              />{" "}
              
              <Route
                path="super-admin/add-group"
                element={<routes.SuperAdminAddGroup />}
              />{" "}
              
              <Route
                path="super-admin/view-groups"
                element={<routes.SuperAdminViewGroups />}
              />

              <Route
                path="super-admin/assign-group-consultants"
                element={<routes.BUAssignGroupConsultants />}
              />

              <Route
                path="business-unit/group-management"
                element={<routes.BUGroupsManagement />}
              />
              <Route
                path="super-admin/summary-report"
                element={<routes.SuperAdminReportsSummary />}
              />
              <Route
                path="super-admin/business-unit-wise-report"
                element={<routes.SuperAdminBusinessUnitWiseReport />}
              />
              <Route
                path="super-admin/view-business-units"
                element={<routes.ViewBusinessUnits />}
              />
              <Route
                path="global-jobs-management/view-jobs"
                element={<routes.AdminGlobalJobManagement />}
              />
              <Route
                path="client/assessments"
                element={<routes.Assessments />}
              />{" "}
              <Route
                path="client/assessments/create"
                element={<routes.CreateAssessment />}
              />{" "}
              <Route
                path="client/assessments/update/:id"
                element={<routes.UpdateAssessment />}
              />
              <Route
                path="author/codingToDebuggingStudio"
                element={<routes.CodingToDebuggingStudio />}
              />
              <Route
                path="client/view-applicants-jobs"
                element={<routes.ViewApplicantsJobs />}
              />
              <Route
                path="agency/share-job-report"
                element={<routes.ShareJobReport />}
              />
              <Route path="coding-problems" element={<routes.Problems />} />
              <Route
                path="coding-problems/ai"
                element={<routes.AiProblems />}
              />
              <Route
                path="coding-problems/ai/create"
                element={<routes.CreateAiCoding />}
              />
              <Route path="admin-sections" element={<routes.AdminSections />} />
              <Route
                path="coding-problems/create"
                element={<routes.CreateProblem />}
              />
              <Route
                path="author/unified-question-creator"
                element={<routes.CreateUnifiedProblem />}
              />
              <Route
                path="author/csv-upload"
                element={<routes.CSVUploadUnified />}
              />
              <Route
                path="author/csv-upload-history"
                element={<routes.CSVUploadHistory />}
              />
              <Route
                path="author/questions"
                element={<routes.UnifiedQuestionBank />}
              />
              <Route
                path="admin/question-bank-overview"
                element={<routes.QuestionBankOverview />}
              />
              <Route
                path="moderator/questions"
                element={<routes.UnifiedModeratorDashboard />}
              />
              <Route
                path="author/questions/details/:id"
                element={<routes.UnifiedQuestionDetail />}
              />
              <Route
                path="author/questions/edit/:id"
                element={<routes.UnifiedQuestionUpdate />}
              />
              <Route
                path="coding-problems/csv-upload"
                element={<routes.CreateCsvProblem />}
              />
              <Route
                path="mcq-problems/csv-upload"
                element={<routes.CreateCsvMcq />}
              />
              <Route path="mcq-problems" element={<routes.MCQs />} />
              <Route path="mcq-problems/ai" element={<routes.AiMCQs />} />
              <Route
                path="mcq-problems/ai/create"
                element={<routes.CreateAiMCQ />}
              />
              <Route path="/mcq/details/:id" element={<routes.MCQDetails />} />
              <Route
                path="mcq-problems/create"
                element={<routes.CreateMCQ />}
              />
              <Route path="mcq/update/:mcqId" element={<routes.UpdateMCQ />} />
              <Route path="mcq/review/:mcqId" element={<routes.ReviewMcq />} />
              <Route path="clients" element={<routes.ClientList />} />
              <Route path="clients/create" element={<routes.CreateClient />} />
              <Route
                path="problem/update/:problemId"
                element={<routes.UpdateProblem />}
              />
              <Route
                path="/career-management/post-job"
                element={<routes.CareerJobPost />}
              />
              <Route
                path="/career-management/view-jobs"
                element={<routes.AdminViewJobs />}
              />
              <Route
                path="/career-management/view-applicants"
                element={<routes.AdminViewApplicants />}
              />
              <Route
                path="/coupon-management/create-coupon"
                element={<routes.CreateCoupon />}
              />
              <Route
                path="/coupon-management"
                element={<routes.ViewCoupons />}
              />
              <Route
                path="admin/client/update/:id"
                element={<routes.UpdateClient />}
              />
              <Route path="client/details" element={<routes.ClientDetails />} />
              <Route
                path="client/assessments"
                element={<routes.Assessments />}
              />
              <Route
                path="client/users"
                element={<routes.ClientUserManagement />}
              />
              <Route
                path="client/user-groups"
                element={<routes.UserGroupManagement />}
              />
              <Route
                path="client/create-users"
                element={<routes.CreateUsersTabs />}
              />
              <Route
                path="client/create-groups"
                element={<routes.CreateGroups />}
              />
              <Route
                path="client/create-group-users"
                element={<routes.CreateGroupUsers />}
              />
              <Route
                path="client/assessments/create"
                element={<routes.CreateAssessment />}
              />
              <Route
                path="client/assessmentsv2/create"
                element={<routes.CreateAssessmentV2 />}
              />
              <Route
                path="client/assessmentsv2/update/:id"
                element={<routes.UpdateAssessmentV2 />}
              />
              <Route
                path="client/assessmentsv2"
                element={<routes.AssessmentsV2 />}
              />
              <Route
                path="client/assessmentsv2/users/:id"
                element={<routes.AssessmentUsers />}
              />
              <Route
                path="client/assessmentsv2/:assessmentId/user/:userId/report"
                element={<routes.AssessmentUserReport />}
              />
              <Route
                path="client/reports/assessment-wise"
                element={<routes.AssessmentWiseUserReport />}
              />
              <Route
                path="client/reports/user-group-wise"
                element={<routes.UserGroupWiseReport />}
              />
              <Route
                path="client/reports/student-wise"
                element={<routes.StudentWiseReport />}
              />
              <Route
                path="client/reports/custom-assessment"
                element={<routes.CustomAssessmentReport />}
              />
              <Route
                path="client/reports/client-wise"
                element={<routes.ClientWiseReport />}
              />
              <Route
                path="client/reports/make-my-report"
                element={<routes.MakeMyReport />}
              />
              <Route
                path="fitment-analysis/content-fitment-analysis"
                element={<routes.ContentFA />}
              />
              <Route
                path="fitment-analysis/company-fitment-analysis"
                element={<routes.CompanyFA />}
              />
              <Route
                path="fitment-analysis/job-role-fitment-analysis"
                element={<routes.JobRoleFA />}
              />
              <Route
                path="fitment-analysis/job-roles-topic"
                element={<routes.JobRolesTopic />}
              />
              <Route
                path="client/assessmentsv2/launchAssessment"
                element={<routes.LaunchAssessmentV2 />}
              />
              <Route
                path="client/assessmentsv2/scheduleAssessment"
                element={<routes.ScheduleAssessmentV2 />}
              />
              <Route
                path="client/assessmentsv2/runningAssessment"
                element={<routes.RunningAssessmentsV2 />}
              />
              <Route
                path="assessment-monitoring/live-monitor/:attemptId"
                element={<routes.LiveMonitor />}
              />
              <Route
                path="assessment-monitoring/students/:assessmentId"
                element={<routes.StudentListForAssessment />}
              />
              <Route
                path="agency/post-job"
                element={<routes.AgencyPostJob />}
              />
              <Route
                path="agency/edit-job"
                element={<routes.AgencyEditJob />}
              />
              <Route
                path="agency/manage-jobs"
                element={<routes.AgencyJobsManagement />}
              />
              <Route
                path="agency/manage-applicants/:jobId"
                element={<routes.AgencyManageApplicants />}
              />
              <Route
                path="agency/job-bulk-add-applicants/:jobId"
                element={<routes.BulkAddApplicants />}
              />
              <Route path="assessments" element={<routes.Assessments />} />
              <Route
                path="assessments/create"
                element={<routes.CreateAssessment />}
              />
              <Route
                path="assessments/update/:id"
                element={<routes.UpdateAssessment />}
              />
              {/* <Route
                path="assessments/report/:id"
                element={<routes.AssessmentReport />}
              /> */}
              <Route
                path="assessments/report/:id/user/:userId"
                element={<routes.UserAssessmentReport />}
              />
              <Route path="client/sections" element={<routes.Sections />} />
              <Route
                path="client/sections/create"
                element={<routes.CreateSection />}
              />
              <Route
                path="client/sections/update/:id"
                element={<routes.UpdateSection />}
              />
              <Route path="client/patterns" element={<routes.Patterns />} />
              <Route
                path="client/patterns/create"
                element={<routes.CreatePattern />}
              />
              <Route
                path="client/patterns/update/:id"
                element={<routes.UpdatePattern />}
              />
              <Route
                path="report-questions"
                element={<routes.QuestionReports />}
              />
              <Route
                path="contact-sales-dashboard"
                element={<routes.ContactSalesDashboard />}
              />
              <Route
                path="request-demo-dashboard"
                element={<routes.RequestDemoDashboard />}
              />
              <Route
                path="question-bank-overview/mcq"
                element={<routes.McqBankOverview />}
              />
              <Route
                path="question-bank-overview/coding"
                element={<routes.CodingBankOverview />}
              />
              <Route path="plans" element={<routes.ManagePlans />} />
              <Route path="plans/create" element={<routes.CreatePlan />} />
              <Route path="plans/update/:id" element={<routes.UpdatePlan />} />
              <Route
                path="mcq-problems/review"
                element={<routes.ReviewMcqs />}
              />
              <Route
                path="mcq-problems/assign-moderator"
                element={<routes.ModeratorDashboard />}
              />
              <Route
                path="mcq-problems/assign-moderator/review/:id"
                element={<routes.ModeratorReviewPage />}
              />
              <Route
                path="coding-problems/assign-moderator"
                element={<routes.CodingModeratorDashboard />}
              />
              <Route
                path="coding-problems/assign-moderator/review/:id"
                element={<routes.CodingModeratorReviewPage />}
              />
              <Route
                path="author-management/summary"
                element={<routes.AuthorManagementSummary />}
              />
              <Route
                path="problem/details/:id"
                element={<routes.ProblemDetails />}
              />
              <Route
                path="coding-problems/view/:id"
                element={<routes.CodingProblemViewer />}
              />
              <Route path="tags" element={<routes.Tags />} />
              <Route path="tags/create" element={<routes.CreateTags />} />
              <Route path="tags/update/:id" element={<routes.UpdateTags />} />
              <Route path="companyTags" element={<routes.companyTags />} />
              <Route
                path="companyTags/create"
                element={<routes.CreateCompanyTags />}
              />
              <Route
                path="companyTags/update/:id"
                element={<routes.UpdateCompanyTags />}
              />
              <Route path="admin/user/:id" element={<routes.UserPage />} />
              <Route
                path="work-tracker"
                element={<routes.AuthorContributions />}
              />
            </Route>

            {/* Miscellaneous Routes */}
            <Route path="/proctor" element={<routes.ProctorApp />} />
            <Route
              path="/eye-tracking"
              element={<routes.EyeTrackingComponent />}
            />
            <Route path="/test" element={<routes.TestApp />} />
            <Route path="*" element={<routes.NotFoundPage />} />
          </Routes>
          )}
        </Suspense>

        {/* Add it here so it's always rendered */}
        {/* <Toaster position="top-right" richColors /> */}
      </Router>
    </ThemeContextProvider>
  );
};

export default App;
